import { isEqual } from "lodash-es";

import type { BaseResults, ExactResults, SearchFilter, UpsellResult } from "@/types";

interface NewSearch {
  query: string;
  filter?: SearchFilter;
  staticMetadata: Record<string, string>;
  temporaryMetadata: Record<string, string[]>;
  section: string | undefined;
}

interface ChangedSearch extends NewSearch {
  dictionaryId?: string;
}

export function hasQueryChanged(current: string, next: string): boolean {
  return current.toLowerCase() !== next.toLowerCase();
}

export function hasFilterChanged(current?: SearchFilter, next?: SearchFilter): boolean {
  return !isEqual(current, next);
}

export function isNewSearch(current: Partial<NewSearch>, next: Partial<NewSearch>, checkMetadata = true): boolean {
  if (hasQueryChanged(current.query ?? "", next.query ?? "")) {
    return true;
  }

  if (hasFilterChanged(current.filter, next.filter)) {
    return true;
  }

  if (current.section !== next.section) {
    return true;
  }

  if (!checkMetadata) {
    return false;
  }

  if (!isEqual(current.staticMetadata, next.staticMetadata)) {
    return true;
  }

  if (!isEqual(current.temporaryMetadata, next.temporaryMetadata)) {
    return true;
  }

  return false;
}

export function hasAnyMethodIdsExactArticleResults(methodIds?: string[], results?: BaseResults): boolean {
  if (!results || !isExactResults(results)) {
    return false;
  }

  return hasAnyMethodIdsArticleResults(methodIds, results);
}

export function hasAnyMethodIdsUpsellArticleResults(methodIds?: string[], results?: UpsellResult): boolean {
  return hasAnyMethodIdsArticleResults(methodIds, results);
}

function hasAnyMethodIdsArticleResults(methodIds?: string[], results?: ExactResults | UpsellResult): boolean {
  if (!methodIds || methodIds.length === 0 || !results) {
    return false;
  }

  // None of the method ids has any results
  if (!methodIds.some((methodId) => results.methodIdsWithResult.includes(methodId))) {
    return false;
  }

  // None of the method ids has any articles
  return methodIds.some((methodId) => results.articles.some((article) => article.methodId === methodId));
}

function isExactResults(results: BaseResults): results is ExactResults {
  return "type" in results && results.type === "exact";
}

export function hasAllMethodIdsNoResult(methodIds?: string[], results?: BaseResults): boolean {
  if (!methodIds || methodIds.length === 0) {
    return false;
  }

  if (!results) {
    return false;
  }

  const methodIdsWithResult = results.methodIdsWithResult;
  return !methodIds.some((methodId) => methodIdsWithResult.includes(methodId));
}

export function hasSearchChanged(
  current: Partial<ChangedSearch>,
  next: Partial<ChangedSearch>,
  checkMetadata = true
): boolean {
  return isNewSearch(current, next, checkMetadata) || current.dictionaryId !== next.dictionaryId;
}

export function mergeSearchMetadata(
  staticMetadata: Record<string, string>,
  temporaryMetadata: Record<string, string[]>
): Record<string, string[]> {
  const merged: Record<string, string[]> = {};

  for (const methodId in staticMetadata) {
    merged[methodId] = [staticMetadata[methodId]];
  }

  for (const methodId in temporaryMetadata) {
    if (!merged[methodId]) {
      merged[methodId] = [];
    }

    merged[methodId].push(...temporaryMetadata[methodId]);
  }

  return merged;
}
